import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { err, ok } from 'neverthrow';
import { v4 } from 'uuid';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { toast } from '@cotera/client/app/components/ui';
import {
  prepareChatUploads,
  type ChatUploadPart,
} from '../upload-chat-dataset';
import {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
  BaseAction,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import type { InitialArtifactParams, InitialAction } from '@cotera/sdk/core';
import { Parser } from '@cotera/era';
import { setPendingChatSubmission } from '../models/pending-chat-submission';
import {
  getMessageLengthFromPayload,
  hasContentToSubmit,
} from '../utils/prompt-utils';
import { FolderContentsPageModel } from '../../folder/folder-contents-page.model';
import {
  resolveResourceNavigator,
  type ResourceNavigator,
} from '../../resources/navigation';
import { ResourceType } from '@cotera/api';

type Params = {
  text?: string;
  editorState?: { type: 'doc'; content?: unknown[] };
  files?: Array<Record<string, unknown>>;
  autoApproveTools?: boolean;
  /** When true, the new chat starts in plan mode (architecture skills + plan-first behavior). */
  planMode?: boolean;
  /** Artifacts to show in the artifact panel when the chat opens (e.g. agent when navigating from agent page) */
  initialArtifacts?: InitialArtifactParams[];
  /** Tool keys to pre-load (e.g. coco/<agentKey> when navigating from agent page) */
  initialToolKeys?: string[];
  /** Actions to apply when the chat is created (e.g. focus_agent when navigating from agent page) */
  initialActions?: InitialAction[];
  /** Model to use for the chat (overrides default) */
  model?: string;
  /** When set, chat will be assigned to this folder once created (first message). Passed in nav state. */
  folderId?: string;
  /** Saved agent id for chat context.agentId (e.g. from agent page). */
  agentId?: string;
  /**
   * Open the chat with its seeded artifacts/tools in place but DON'T auto-submit
   * a first message — the user lands on a ready chat with the panel already
   * populated (e.g. the Library "Organize" button seeds the review panel).
   */
  seedOnly?: boolean;
  /**
   * Use this conversation id instead of generating one. Lets the caller PREFETCH
   * per-conversation data (keyed by this id) before navigating, so the chat
   * opens with it already warm.
   */
  chatId?: string;
};

export class NewChatAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'New Chat';
  description = 'Start a new conversation';
  inputSchema = z.object({
    text: z.string().optional(),
    editorState: z
      .object({
        type: z.literal('doc'),
        content: z.array(z.unknown()).optional(),
      })
      .optional(),
    files: z.array(z.record(z.string(), z.unknown())).optional(),
    autoApproveTools: z.boolean().optional(),
    planMode: z.boolean().optional(),
    initialArtifacts: z
      .array(
        z.object({
          artifactType: z.string(),
          artifactId: z.string().optional(),
          data: z.unknown().optional(),
        })
      )
      .optional(),
    initialToolKeys: z.array(z.string()).optional(),
    initialActions: z
      .array(
        z.discriminatedUnion('type', [
          z.object({ type: z.literal('focus_agent'), agentId: z.string() }),
          z.object({ type: z.literal('unfocus_agent') }),
          z.object({
            type: z.literal('output_shape'),
            shape: Parser.ExtendedAttributeTypeSchema,
          }),
          z.object({ type: z.literal('auto_approve') }),
          z.object({ type: z.literal('remove_auto_approve') }),
        ])
      )
      .optional(),
    model: z.string().optional(),
    folderId: z.string().uuid().optional(),
    agentId: z.string().uuid().optional(),
    seedOnly: z.boolean().optional(),
    chatId: z.string().uuid().optional(),
  });
  group = 'Chat';
  icon = 'chat';

  constructor(
    private readonly fallbackNavigator: ResourceNavigator,
    private readonly datasetClient: TenantedClient['resource']['dataset']
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const chatId = payload.chatId ?? v4();
    const initialArtifacts =
      Array.isArray(payload.initialArtifacts) &&
      payload.initialArtifacts.length > 0
        ? payload.initialArtifacts
        : undefined;

    const initialActions = payload.initialActions;

    if (
      hasContentToSubmit({
        ...payload,
        initialArtifacts,
        initialToolKeys: payload.initialToolKeys,
        initialActions,
      }) ||
      payload.model !== undefined ||
      payload.seedOnly === true
    ) {
      // Upload any tabular attachments now, while the real File is still in
      // memory. The submission below is JSON-serialized into sessionStorage,
      // which would drop the bytes; `prepareChatUploads` leaves a lightweight
      // receipt in each uploaded file's place. The dataset is scoped to chatId,
      // so the chat turn posts its placeholder once the page loads.
      let files = payload.files;
      const rawFiles = payload.files;
      if (Array.isArray(rawFiles) && rawFiles.length > 0) {
        try {
          const prepared = await prepareChatUploads(
            this.datasetClient,
            chatId,
            rawFiles as ChatUploadPart[]
          );
          files = prepared.pendingParts;
        } catch (uploadError) {
          const message =
            uploadError instanceof Error
              ? uploadError.message
              : 'Failed to upload attachment';
          toast.error(message);
          return err([{ t: 'error', message }]);
        }
      }

      setPendingChatSubmission(chatId, {
        text: payload.text,
        editorState: payload.editorState,
        files,
        initialArtifacts,
        initialToolKeys: payload.initialToolKeys,
        initialActions,
        model: payload.model,
        ...(payload.agentId !== undefined && { agentId: payload.agentId }),
        ...(payload.seedOnly === true && { seedOnly: true }),
      });
    }

    const folderIdFromContext = (() => {
      const folderModel = context.targetedModelOfType(FolderContentsPageModel);
      const folderId = folderModel?.folderId;
      return folderId ?? undefined;
    })();

    // Chats have no artifact renderer, so this always falls through to a page
    // navigation — it goes through the navigator only so every create-action
    // shares one API.
    resolveResourceNavigator(context, this.fallbackNavigator).open({
      resourceType: ResourceType.Chat,
      resourceId: chatId,
      state: {
        newChat: true,
        autoApproveTools: payload.autoApproveTools,
        planMode: payload.planMode,
        ...(payload.folderId !== undefined && { folderId: payload.folderId }),
        ...(payload.folderId === undefined &&
          folderIdFromContext !== undefined && {
            folderId: folderIdFromContext,
          }),
      },
    });
    return ok({ next: true, resourceId: chatId });
  }

  track({
    payload,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const messageLength = getMessageLengthFromPayload(payload);
    return {
      event: CocoEvent.NewChatCreated,
      properties: {
        hasInitialMessage: messageLength > 0,
        messageLength,
        autoApproveTools: payload.autoApproveTools ?? false,
      },
    };
  }
}
